/**
 * AdoraCare - TREATMENT-CATALOG-AND-PLAN-PICKER-V1 (V6-P3): the doctor
 * treatment-picker view model.
 *
 * Pure, React-free helpers for the coded plan picker on the consult
 * workspace. Mirrors doctor-home-view.ts / case-sheet-cockpit-view.ts
 * (unit-tested by direct call; the component renders what these derive).
 *
 * THE PICKER MODES (D-6 FREE-TEXT RECONCILIATION, the documented call):
 *   - catalog unavailable (pre-20260711_3) OR catalog empty -> the LEGACY
 *     free-text add form renders BYTE-IDENTICAL to today (fail-safe law;
 *     test-pinned).
 *   - catalog populated -> the PICKER is the primary path (search ->
 *     select -> amount). New free-text entry is RETAINED as an explicit
 *     escape INSIDE the one add flow ("Not in the catalog - add by name"):
 *     a mid-consult doctor is never blocked by an incomplete catalog
 *     (tenant catalog completeness is unproven until Deepak's price master
 *     lands -- question 4), and legacy rows must keep rendering honestly
 *     forever. ONE form, ONE submit path -- never two parallel entry UIs.
 *
 * MONEY (integer only):
 *   - catalog defaults are INTEGER MINOR UNITS; display reuses THE ONE
 *     billing formatter's digits through the doctor money grammar
 *     (rupeesFromMinorLabel over formatMinorUnitsInr -- quotient +
 *     remainder, never float math; DOCTOR-MONEY-CONFORMANCE-V1).
 *   - the plan estimate stays WHOLE INR rupees; a default seeds the
 *     editable estimate ONLY when it is an exact whole-rupee multiple
 *     (catalogDefaultToWholeRupees-equivalent rule inlined here for the
 *     frontend); a paise-precision default displays exactly and seeds
 *     nothing (no silent truncation of money).
 *   - a NULL default renders "No default price configured" -- NEVER zero.
 */

import type {
  ClinicalPlanItemView,
  TreatmentCatalogItemView,
} from '@/dashboard/api';
import { rupeesFromMinorLabel } from '@/dashboard/doctor/doctor-consult-view';
import { formatWholeInr } from '@/dashboard/clinical-workbench/case-sheet-cockpit-view';

// --- pricing-unit display ----------------------------------------------------

/** Neutral display labels for the controlled pricing-unit vocabulary. */
export function pricingUnitLabel(unit: string | null): string {
  switch (unit) {
    case 'procedure':
      return 'per procedure';
    case 'unit':
      return 'per unit';
    case 'session':
      return 'per session';
    case 'visit':
      return 'per visit';
    case 'case':
      return 'per case';
    default:
      return '';
  }
}

// --- catalog default display + seeding (integer money) ------------------------

/** The honest no-price copy (UNKNOWN PRICE LAW: never a fake zero). */
export const CATALOG_NO_DEFAULT_COPY = 'No default price configured';

/** Display a catalog default: 'Rs 4,500' / 'Rs 4,500.50' -- THE ONE
 *  minor-units formatter's digits in the doctor money grammar -- or the
 *  honest not-configured copy. */
export function catalogDefaultLabel(
  defaultAmountMinorUnits: number | null | undefined,
): string {
  if (
    defaultAmountMinorUnits === null ||
    defaultAmountMinorUnits === undefined ||
    !Number.isInteger(defaultAmountMinorUnits) ||
    defaultAmountMinorUnits < 0
  ) {
    return CATALOG_NO_DEFAULT_COPY;
  }
  return rupeesFromMinorLabel(defaultAmountMinorUnits);
}

/**
 * The whole-rupee seed for the editable estimate input: the default's exact
 * rupee value when (and ONLY when) it is an exact multiple of 100 minor
 * units; null otherwise (no default, or paise precision the whole-INR plan
 * spine cannot represent -- the doctor types the patient-specific amount).
 * Integer arithmetic only.
 */
export function catalogDefaultSeedRupees(
  defaultAmountMinorUnits: number | null | undefined,
): number | null {
  if (
    defaultAmountMinorUnits === null ||
    defaultAmountMinorUnits === undefined ||
    !Number.isInteger(defaultAmountMinorUnits) ||
    defaultAmountMinorUnits < 0
  ) {
    return null;
  }
  const remainder = defaultAmountMinorUnits % 100;
  if (remainder !== 0) return null;
  return (defaultAmountMinorUnits - remainder) / 100;
}

// --- price source (CLINIC-CATALOG-PRICING-TRUTH-S1-V1) -------------------------

/**
 * The price SOURCE of a catalog item on a CLINIC-RESOLVED read (the
 * ?clinicId picker/browse context) -- always visible, never a silently
 * substituted number (the S1 read-truth law):
 *   - 'clinic_price'   -> a clinic override row priced this (clinicId
 *                         non-null; D-6: clinic price ALWAYS wins);
 *   - 'chain_rate'     -> the org-wide default priced it. LAWFUL label by
 *                         SEED CONSTRUCTION: D-6 populates the org default
 *                         ONLY from a rate the tariff itself states
 *                         chain-wide (e.g. CONSULTATION = 0.00) -- never an
 *                         averaged or invented number;
 *   - 'not_configured' -> no price exists on the resolved row (NULL --
 *                         the 20260711_3 UNKNOWN PRICE LAW). Renders the
 *                         ruled copy VISIBLY -- never a blank, never a
 *                         crash, never a substituted number.
 */
export type CatalogPriceSource = 'clinic_price' | 'chain_rate' | 'not_configured';

/** The founder-ruled gap copy for an UNSET price (W-B3,
 *  TREATMENT-CATALOG-PRICING-COMPOSER-V2, founder order 2026-08-01 --
 *  supersedes the 2026-07-27 'Price not configured' wording for price
 *  displays; the state semantics are unchanged: NULL never renders as a
 *  fake zero). */
export const PRICE_NOT_CONFIGURED_COPY = 'Price not set';

/** W-B3/S-4: no in-product price writer exists for ANY role (the catalog
 *  is read-only -- founder Studio seed; the V5-P5/V8-P7 admin seam), so an
 *  unset-price state NAMES who can fix it instead of offering a dead
 *  action. */
export const PRICE_SETTER_NAMING_COPY =
  'Set by the org administrator via the tariff seed.';

export function catalogPriceSource(
  item: Pick<TreatmentCatalogItemView, 'clinicId' | 'defaultAmountMinorUnits'>,
): CatalogPriceSource {
  if (
    item.defaultAmountMinorUnits === null ||
    item.defaultAmountMinorUnits === undefined ||
    !Number.isInteger(item.defaultAmountMinorUnits) ||
    item.defaultAmountMinorUnits < 0
  ) {
    return 'not_configured';
  }
  return item.clinicId !== null ? 'clinic_price' : 'chain_rate';
}

/** Controlled display labels for the source chip (FIELDS, NOT SENTENCES). */
export function catalogPriceSourceLabel(source: CatalogPriceSource): string {
  switch (source) {
    case 'clinic_price':
      return 'Clinic price';
    case 'chain_rate':
      return 'Chain rate';
    case 'not_configured':
      return PRICE_NOT_CONFIGURED_COPY;
  }
}

/** The price display for a catalog surface: the formatted amount (THE ONE
 *  minor-units formatter's digits in the doctor money grammar) or the
 *  VISIBLE ruled gap copy -- never a blank, never a fake zero. */
export function catalogPriceDisplay(
  item: Pick<TreatmentCatalogItemView, 'clinicId' | 'defaultAmountMinorUnits'>,
): string {
  if (catalogPriceSource(item) === 'not_configured') {
    return PRICE_NOT_CONFIGURED_COPY;
  }
  return rupeesFromMinorLabel(item.defaultAmountMinorUnits as number);
}

// --- picker search -------------------------------------------------------------

/**
 * Case-insensitive substring filter over name / code / group. Blank query =
 * everything (the bounded list is already org-fenced + capped server-side).
 */
export function filterCatalogItems(
  items: readonly TreatmentCatalogItemView[],
  query: string,
): readonly TreatmentCatalogItemView[] {
  const q = query.trim().toLowerCase();
  if (q === '') return items;
  return items.filter(
    (i) =>
      i.serviceName.toLowerCase().includes(q) ||
      i.serviceCode.toLowerCase().includes(q) ||
      i.serviceGroup.toLowerCase().includes(q),
  );
}

// --- progressive disclosure (the S1 UX LAW) ------------------------------------

/**
 * PROGRESSIVE DISCLOSURE (binding for every catalog surface, founder
 * rulings 2026-07-27): the visible workflow is STEPWISE DEPENDENT
 * selection -- clinic scope -> service group -> procedure -> only the
 * selected procedure's price/provenance fields. The full catalog is NEVER
 * rendered flat; the next level's choices appear only after the previous
 * choice is made; search narrows WITHIN the current scope. The group
 * level derives from the TARIFF'S OWN grouping column as normalized
 * (service_group -- never hardcoded group strings).
 */

export interface CatalogGroupSummary {
  readonly group: string;
  readonly itemCount: number;
}

/** The group level: distinct service groups of the (already-ordered)
 *  bounded list, with item counts. Derived from data, never hardcoded. */
export function catalogGroupSummaries(
  items: readonly TreatmentCatalogItemView[],
): readonly CatalogGroupSummary[] {
  return groupCatalogItems(items).map((g) => ({
    group: g.group,
    itemCount: g.items.length,
  }));
}

/** Search at the GROUP level narrows the group list: a group matches when
 *  its name matches OR any member procedure matches (the member is what
 *  the operator is really hunting). */
export function filterCatalogGroups(
  items: readonly TreatmentCatalogItemView[],
  query: string,
): readonly CatalogGroupSummary[] {
  const q = query.trim().toLowerCase();
  if (q === '') return catalogGroupSummaries(items);
  return catalogGroupSummaries(items).filter((g) => {
    if (g.group.toLowerCase().includes(q)) return true;
    return itemsInGroup(items, g.group).some(
      (i) =>
        i.serviceName.toLowerCase().includes(q) ||
        i.serviceCode.toLowerCase().includes(q),
    );
  });
}

/** The procedure level: ONLY the selected group's items (the previous
 *  choice constrains the next level). */
export function itemsInGroup(
  items: readonly TreatmentCatalogItemView[],
  group: string,
): readonly TreatmentCatalogItemView[] {
  return items.filter((i) => i.serviceGroup === group);
}

/** Search at the PROCEDURE level narrows WITHIN the selected group only. */
export function filterItemsWithinGroup(
  items: readonly TreatmentCatalogItemView[],
  group: string,
  query: string,
): readonly TreatmentCatalogItemView[] {
  const scoped = itemsInGroup(items, group);
  const q = query.trim().toLowerCase();
  if (q === '') return scoped;
  return scoped.filter(
    (i) =>
      i.serviceName.toLowerCase().includes(q) ||
      i.serviceCode.toLowerCase().includes(q),
  );
}

// --- W-B1 type-ahead (TREATMENT-CATALOG-PRICING-COMPOSER-V2) -------------------

/**
 * One group section of the type-ahead result: procedures matching the query
 * by NAME or CODE (server order preserved), under their own group heading.
 * A group whose NAME matches while no member does enters with items: [] --
 * it renders as a browsable group door, never a flat member dump.
 *
 * RECONCILIATION with the S1 PROGRESSIVE-DISCLOSURE LAW (binding): the
 * RESTING entry state stays the group list (a blank query returns []);
 * only a typed query surfaces procedure hits, grouped under their own
 * headings -- the full catalog still never renders flat. The V6-P3 picker
 * contract always admitted search by name/code; W-B1 (founder walk
 * 2026-07-31/08-01) makes the hit DIRECTLY selectable instead of a
 * two-hop group drill.
 *
 * ORDERING (the W-B1 conditional): results keep the served lawful order
 * (service_group, service_name, id -- api/routes/treatment-catalog.ts).
 * No verified usage signal or existing configuration provides a
 * common-first ordering, so none is invented; common-first is a named
 * follow-up in the run record.
 */
export interface CatalogTypeaheadGroup {
  readonly group: string;
  /** Member procedures matching the query by name/code (may be empty when
   *  only the group name matched). */
  readonly items: readonly TreatmentCatalogItemView[];
  /** FULL member count of the group (the browse-door affordance). */
  readonly itemCount: number;
}

export function typeaheadCatalogResults(
  items: readonly TreatmentCatalogItemView[],
  query: string,
): readonly CatalogTypeaheadGroup[] {
  const q = query.trim().toLowerCase();
  if (q === '') return [];
  const out: CatalogTypeaheadGroup[] = [];
  for (const g of groupCatalogItems(items)) {
    const matched = g.items.filter(
      (i) =>
        i.serviceName.toLowerCase().includes(q) ||
        i.serviceCode.toLowerCase().includes(q),
    );
    if (matched.length > 0) {
      out.push({ group: g.group, items: matched, itemCount: g.items.length });
    } else if (g.group.toLowerCase().includes(q)) {
      out.push({ group: g.group, items: [], itemCount: g.items.length });
    }
  }
  return out;
}

/** The flat procedure hits in visual order (keyboard highlight walks this). */
export function typeaheadFlatItems(
  groups: readonly CatalogTypeaheadGroup[],
): readonly TreatmentCatalogItemView[] {
  return groups.flatMap((g) => g.items);
}

/** The keyboard fast path: Enter with nothing highlighted picks the FIRST
 *  procedure hit (type -> Enter -> picked; the five-second law W-B5). */
export function typeaheadFirstItem(
  groups: readonly CatalogTypeaheadGroup[],
): TreatmentCatalogItemView | null {
  return typeaheadFlatItems(groups)[0] ?? null;
}

/** Group the (already group+name ordered) items for the grouped list render. */
export function groupCatalogItems(
  items: readonly TreatmentCatalogItemView[],
): ReadonlyArray<{
  readonly group: string;
  readonly items: readonly TreatmentCatalogItemView[];
}> {
  const groups: Array<{ group: string; items: TreatmentCatalogItemView[] }> = [];
  for (const item of items) {
    const last = groups[groups.length - 1];
    if (last !== undefined && last.group === item.serviceGroup) {
      last.items.push(item);
    } else {
      groups.push({ group: item.serviceGroup, items: [item] });
    }
  }
  return groups;
}

// --- amount provenance (default vs edited vs legacy) ---------------------------

/**
 * The provenance of a plan item's estimate, for the FIELDS-NOT-SENTENCES
 * distinction chip (Scope 3: an edited amount must visibly distinguish the
 * catalog default from the patient-specific proposed amount):
 *   - 'legacy'          -> free-text item (no catalog snapshot);
 *   - 'no_default'      -> coded, but no default was configured at selection
 *                          (the estimate, if any, is the doctor's);
 *   - 'catalog_default' -> the estimate equals the snapshot default exactly
 *                          (whole-rupee compare by integer math);
 *   - 'edited'          -> coded with a default, and the estimate differs
 *                          (or is absent) -- the patient-specific amount.
 */
export type PlanAmountProvenance =
  | 'legacy'
  | 'no_default'
  | 'catalog_default'
  | 'edited';

export function planAmountProvenance(
  item: Pick<
    ClinicalPlanItemView,
    'estimatedValue' | 'catalogCode' | 'catalogDefaultAmountMinorUnits'
  >,
): PlanAmountProvenance {
  const code = item.catalogCode ?? null;
  if (code === null) return 'legacy';
  const def = item.catalogDefaultAmountMinorUnits ?? null;
  if (def === null) return 'no_default';
  if (
    item.estimatedValue !== null &&
    Number.isInteger(item.estimatedValue) &&
    item.estimatedValue * 100 === def
  ) {
    return 'catalog_default';
  }
  return 'edited';
}

/** The distinction chip copy per provenance ('' = no chip renders). */
export function planAmountProvenanceLabel(
  provenance: PlanAmountProvenance,
  catalogDefaultAmountMinorUnits: number | null | undefined,
): string {
  switch (provenance) {
    case 'catalog_default':
      return 'Catalog default';
    case 'edited': {
      const def = catalogDefaultAmountMinorUnits ?? null;
      return def === null
        ? 'Edited'
        : `Edited - default ${rupeesFromMinorLabel(def)}`;
    }
    case 'no_default':
      return CATALOG_NO_DEFAULT_COPY;
    case 'legacy':
      return '';
  }
}

/**
 * SOURCE-AWARE provenance (CLINIC-CATALOG-PRICING-TRUTH-S1-V1): resolve a
 * SAVED plan item's price source by matching its snapshot catalogCode
 * against the ALREADY-LOADED clinic-resolved catalog (the consult
 * workspace holds it for the picker -- no extra read, no new wire). The
 * resolved item's clinicId says whether THIS clinic's override or the
 * chain-wide org rate priced it. Null when the code no longer resolves
 * (deactivated / replaced since selection) -- the caller falls back to the
 * plain 'Catalog default' chip; the snapshot itself stays the truth
 * (snapshot-on-select: catalog edits never rewrite plan history).
 */
export function resolvePlanItemPriceSource(
  planItem: Pick<ClinicalPlanItemView, 'catalogCode'>,
  catalogItems: readonly TreatmentCatalogItemView[] | null,
): CatalogPriceSource | null {
  const code = planItem.catalogCode ?? null;
  if (code === null || catalogItems === null) return null;
  const match = catalogItems.find((i) => i.serviceCode === code);
  if (match === undefined) return null;
  return catalogPriceSource(match);
}

/**
 * The provenance chip with the S1 source vocabulary layered IN PLACE over
 * the existing four-token vocabulary (never a second chip system):
 *   - 'catalog_default' + a resolvable source -> 'Clinic price' /
 *     'Chain rate' (the D-6 read-truth labels);
 *   - 'edited' stays the manual estimated_value concept (the tenant
 *     NEGOTIATED-AMOUNT distinction) -- existing copy unchanged;
 *   - everything else falls back to planAmountProvenanceLabel verbatim.
 */
export function planAmountProvenanceSourceLabel(
  provenance: PlanAmountProvenance,
  catalogDefaultAmountMinorUnits: number | null | undefined,
  source: CatalogPriceSource | null,
): string {
  if (
    provenance === 'catalog_default' &&
    (source === 'clinic_price' || source === 'chain_rate')
  ) {
    return catalogPriceSourceLabel(source);
  }
  return planAmountProvenanceLabel(provenance, catalogDefaultAmountMinorUnits);
}

// --- picker state derivation ----------------------------------------------------

/**
 * Which add-flow the plan section renders (the D-6 reconciliation):
 *   - 'legacy_form'  -> catalog unavailable OR empty: today's free-text add
 *                       form, byte-identical (fail-safe law, test-pinned);
 *   - 'picker'       -> catalog populated: the coded picker is primary (the
 *                       free-text escape lives INSIDE it).
 */
export type PlanAddMode = 'legacy_form' | 'picker';

export function planAddMode(
  catalog: {
    readonly available: boolean;
    readonly items: readonly TreatmentCatalogItemView[];
  } | null,
): PlanAddMode {
  if (catalog === null || !catalog.available || catalog.items.length === 0) {
    return 'legacy_form';
  }
  return 'picker';
}

// --- W-B3 plan-total truth (TREATMENT-CATALOG-PRICING-COMPOSER-V2) -------------

/** A plan-item price for display: the whole-INR amount, or the unset-price
 *  state VISIBLY (never a blank, never a fake zero). A stored 0 is a TRUE
 *  ZERO (the D-14 actual-zero rate) and renders as a real amount. */
export function planItemPriceDisplay(estimatedValue: number | null): string {
  if (estimatedValue === null || !Number.isFinite(estimatedValue)) {
    return PRICE_NOT_CONFIGURED_COPY;
  }
  return formatWholeInr(estimatedValue);
}

/**
 * The plan-total line (W-B3): totals sum ONLY set prices and SAY PLAINLY
 * when items are excluded as unpriced. A plan whose priced subset is empty
 * never fabricates "total 0" from NULLs; a priced subset genuinely summing
 * to 0 (true-zero rates) renders the real amount.
 */
export function planTotalDisplay(
  items: ReadonlyArray<Pick<ClinicalPlanItemView, 'estimatedValue'>>,
): string {
  let priced = 0;
  let unpriced = 0;
  let total = 0;
  for (const i of items) {
    if (typeof i.estimatedValue === 'number') {
      priced += 1;
      total += i.estimatedValue;
    } else {
      unpriced += 1;
    }
  }
  if (items.length === 0) return '';
  if (priced === 0) {
    return `Plan total: no priced items yet (${unpriced} unpriced).`;
  }
  const base = `Plan total ${formatWholeInr(total)}`;
  return unpriced > 0
    ? `${base} - excludes ${unpriced} unpriced item${unpriced === 1 ? '' : 's'}.`
    : `${base}.`;
}
